// src/App.tsx

import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useToast } from './context/ToastContext';
import { useAuth } from './context/AuthContext';
import { routeForPushData } from './utils/pushRouting';
import Landing from './pages/Landing/Landing';
import Home from './pages/Home/Home';
import Login from './pages/Login/Login';
import PnlMonitor from './pages/PnlMonitor/PnlMonitor';
import Predictions from './pages/Predictions/Predictions';
import Trades from './pages/Trades/Trades';
import Reports from './pages/Reports/Reports';
import Earnings from './pages/Earnings/Earnings';
import Positions from './pages/Positions/Positions';
import PullbackStrategy from './pages/PullbackStrategy/PullbackStrategy';
import PotentialOutcomes from './pages/PotentialOutcomes/PotentialOutcomes';
import Glossary from './pages/Glossary/Glossary';
import Design from './pages/Design/Design';
import Scanner from './pages/Scanner/Scanner';
import QuickTrade from './pages/QuickTrade/QuickTrade';
import Screener from './pages/Screener/Screener';
import Calibration from './pages/Calibration/Calibration';
import DailyEdge from './pages/DailyEdge/DailyEdge';
import LearnHub from './pages/Learn/LearnHub';
import LearnPaperVsLive from './pages/Learn/pages/PaperVsLive';
import LearnAiScreener from './pages/Learn/pages/AiScreener';
import LearnReadingSignals from './pages/Learn/pages/ReadingSignals';
import LearnCalibration from './pages/Learn/pages/Calibration';
import Beanstalk from './pages/Beanstalk/Beanstalk';
import TermsOfService from './pages/Legal/TermsOfService';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';
import RiskDisclosure from './pages/Legal/RiskDisclosure';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import DemoRoute from './components/DemoRoute/DemoRoute';
import OnboardingGuard from './components/OnboardingGuard/OnboardingGuard';
import Navbar from './components/Navbar/Navbar';
import SpyTicker from './components/SpyTicker/SpyTicker';
import { AlpacaProvider } from './context/AlpacaContext';
import { DemoProvider } from './context/DemoContext';
import Onboarding from './pages/Onboarding/Onboarding';
import OAuthCallback from './pages/OAuthCallback/OAuthCallback';
import Settings from './pages/Settings/Settings';
import Market from './pages/Market/Market';
import Strategist from './pages/Strategist/Strategist';
import Scalper from './pages/Scalper/Scalper';
import TradeCalendarPage from './pages/TradeCalendar/TradeCalendarPage';
import Leaderboard from './pages/Leaderboard/Leaderboard';
import ErrorLog from './pages/ErrorLog/ErrorLog';
import Watchlist from './pages/Watchlist/Watchlist';
import InstallPrompt from './components/InstallPrompt/InstallPrompt';
import BrokerAuthBanner from './components/BrokerAuthBanner/BrokerAuthBanner';
import Footer from './components/Footer/Footer';
import CommandPalette from './components/CommandPalette/CommandPalette';
import { TourProvider } from './context/TourContext';
import TourController from './components/Tour/TourController';
import 'shepherd.js/dist/css/shepherd.css';
import './components/Tour/tourStyles.css';

const APP_NAME = 'SignalDeck';

const PAGE_TITLES: Record<string, string> = {
  '/': 'Welcome',
  '/login': 'Sign In',
  '/home': 'Dashboard',
  '/design': 'Design System',
  '/positions': 'Positions',
  '/pnl-monitor': 'P&L Monitor',
  '/predictions': 'Predictions',
  '/trades': 'Trades',
  '/reports': 'Reports',
  '/earnings': 'Earnings',
  '/pullback': 'Pullback Strategy',
  '/potential-outcomes': 'Potential Outcomes',
  '/glossary': 'Glossary',
  '/scanner': 'Market Scanner',
  '/market': 'Market',
  '/quick-trade': 'Quick Trade',
  '/screener': 'AI Screener',
  '/calibration': 'AI Calibration',
  '/daily-edge': 'Daily Edge',
  '/learn': 'Learn SignalDeck',
  '/beanstalk': 'Beanstalk',
  '/strategist': 'Strategist',
  '/scalper': 'AI Scalper',
  '/trade-calendar': 'Trade Calendar',
  '/leaderboard': 'Leaderboard',
  '/errors': 'Error Log',
  '/watchlist': 'Watchlist',
  '/onboarding': 'Get Started',
  '/auth/alpaca/callback': 'Connecting...',
  '/settings': 'Settings',
  '/terms': 'Terms of Service',
  '/privacy': 'Privacy Policy',
  '/risk-disclosure': 'Risk Disclosure',
};

/** Initialize push notifications once after the user is authenticated.
 *  No-ops on web (Capacitor guard in PushService); only meaningful when the
 *  app is running wrapped via Capacitor on iOS/Android. Also wires deep
 *  linking — when the user taps a push, we route to the relevant page. */
const PushNotificationsBootstrap: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    // Fire-and-forget — PushService handles its own error logging.
    import('./services/PushService').then(({ initPushNotifications }) => {
      initPushNotifications({
        onPushAction: (data) => {
          const route = routeForPushData(data);
          if (route) navigate(route);
        },
      });
    });
  }, [user, navigate]);

  return null;
};

/** Listens for session-warning / session-expired custom events and surfaces toasts. */
const SessionTimeoutHandler: React.FC = () => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    const onWarning = () => {
      addToast('Your session will expire in 2 minutes due to inactivity', 'info');
    };

    const onExpired = () => {
      addToast('Session expired — you have been signed out', 'error');
      navigate('/login', { replace: true });
    };

    window.addEventListener('session-warning', onWarning);
    window.addEventListener('session-expired', onExpired);

    return () => {
      window.removeEventListener('session-warning', onWarning);
      window.removeEventListener('session-expired', onExpired);
    };
  }, [user, addToast, navigate]);

  return null;
};

const App: React.FC = () => {
  const location = useLocation();
  const [cmdPaletteOpen, setCmdPaletteOpen] = React.useState(false);

  useEffect(() => {
    const pageTitle = PAGE_TITLES[location.pathname];
    document.title = pageTitle ? `${pageTitle} · ${APP_NAME}` : APP_NAME;
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <DemoProvider>
      <AlpacaProvider>
        <TourProvider>
          <div>
            <SessionTimeoutHandler />
            <PushNotificationsBootstrap />
            <TourController />
            <InstallPrompt />
            <CommandPalette open={cmdPaletteOpen} onClose={() => setCmdPaletteOpen(false)} />
            <div style={{ position: 'sticky', top: 0, zIndex: 100 }}>
              <BrokerAuthBanner />
              <Navbar />
              <SpyTicker />
            </div>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/risk-disclosure" element={<RiskDisclosure />} />
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <Onboarding />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/auth/alpaca/callback"
                element={
                  <ProtectedRoute>
                    <OAuthCallback />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/home"
                element={
                  <DemoRoute>
                    <OnboardingGuard>
                      <Home />
                    </OnboardingGuard>
                  </DemoRoute>
                }
              />
              <Route
                path="/positions"
                element={
                  <DemoRoute>
                    <OnboardingGuard>
                      <Positions />
                    </OnboardingGuard>
                  </DemoRoute>
                }
              />
              <Route
                path="/pnl-monitor"
                element={
                  <DemoRoute>
                    <OnboardingGuard>
                      <PnlMonitor />
                    </OnboardingGuard>
                  </DemoRoute>
                }
              />
              <Route
                path="/predictions"
                element={
                  <DemoRoute>
                    <OnboardingGuard>
                      <Predictions />
                    </OnboardingGuard>
                  </DemoRoute>
                }
              />
              <Route
                path="/trades"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <Trades />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <Reports />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/earnings"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <Earnings />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pullback"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <PullbackStrategy />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/potential-outcomes"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <PotentialOutcomes />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/glossary"
                element={
                  <DemoRoute>
                    <Glossary />
                  </DemoRoute>
                }
              />
              {/* Design system reference — not in nav, dev-only. */}
              <Route path="/design" element={<Design />} />
              <Route path="/learn" element={<LearnHub />} />
              <Route path="/learn/paper-vs-live" element={<LearnPaperVsLive />} />
              <Route path="/learn/ai-screener" element={<LearnAiScreener />} />
              <Route path="/learn/reading-signals" element={<LearnReadingSignals />} />
              <Route path="/learn/calibration" element={<LearnCalibration />} />
              <Route
                path="/scanner"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <Scanner />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/market"
                element={
                  <DemoRoute>
                    <OnboardingGuard>
                      <Market />
                    </OnboardingGuard>
                  </DemoRoute>
                }
              />
              <Route
                path="/quick-trade"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <QuickTrade />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/screener"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <Screener />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/calibration"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <Calibration />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/daily-edge"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <DailyEdge />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/beanstalk"
                element={
                  <DemoRoute>
                    <OnboardingGuard>
                      <Beanstalk />
                    </OnboardingGuard>
                  </DemoRoute>
                }
              />
              <Route
                path="/strategist"
                element={
                  <DemoRoute>
                    <OnboardingGuard>
                      <Strategist />
                    </OnboardingGuard>
                  </DemoRoute>
                }
              />
              <Route
                path="/scalper"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <Scalper />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trade-calendar"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <TradeCalendarPage />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leaderboard"
                element={
                  <DemoRoute>
                    <OnboardingGuard>
                      <Leaderboard />
                    </OnboardingGuard>
                  </DemoRoute>
                }
              />
              <Route
                path="/errors"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <ErrorLog />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/watchlist"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <Watchlist />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
            </Routes>
            <Footer />
          </div>
        </TourProvider>
      </AlpacaProvider>
    </DemoProvider>
  );
};

export default App;
